import React, { useState } from 'react';
import {
  Flex,
  Box,
  Stack,
  Link,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Text,
  useDisclosure,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Spinner,
  Button as ChakraButton,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogCloseButton,
  AlertDialogBody,
  AlertDialogFooter,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Heading,
  Input,
  Select,
  StatusBadge,
  Table,
  Button,
  Pagination,
} from '~/components';
import { ROUTER_PATHS } from '~/constants';
import { EquipmentTypeView } from '~/types/equipmentType';
import {
  useCreateEquipmentType,
  useEquipmentTypes,
  useRoleCan,
  useUpdateEquipmentType,
} from '~/hooks';
import { ConfigureTypes } from '~/pages/Portal/Admin/Equipments/ConfigureTypes/ConfigureTypes';
import { ActiveEquipaments } from '~/pages/Portal/Admin/Equipments/ActiveEquipaments';

type UpdateFormData = {
  nm_tipoequipamento: string;
  tp_situacao: string;
};

export const EquipmentTypes: React.VFC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(true);
  const { data, isLoading, error } = useEquipmentTypes(page);
  const updateEquipmentType = useUpdateEquipmentType();
  const createEquipmentType = useCreateEquipmentType();
  const [isEdition, setIsEdition] = useState(false);
  const { register, handleSubmit, formState, setValue, reset } = useForm();
  const { errors } = formState;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentType, setCurrentType] = useState<EquipmentTypeView | undefined>(
    undefined,
  );
  const {
    isOpen: isDialogOpen,
    onOpen: onDialogOpen,
    onClose: onDialogClose,
  } = useDisclosure();

  const userIsAdmin = useRoleCan({ roles: ['administrador'] });

  function handleDialog(type: EquipmentTypeView) {
    setIsEdition(true);
    onDialogOpen();
    setCurrentType(type);
    setValue('nm_tipoequipamento', type.nm_tipoequipamento);
    setValue('tp_situacao', type.tp_situacao);
    setStatus(type.status);
  }

  function handleDialogCreate() {
    setIsEdition(false);
    reset();
    onDialogOpen();
  }

  function handleSave(formData: UpdateFormData) {
    if (isEdition) {
      updateEquipmentType.mutateAsync({
        id: String(currentType?.id_tipoequipamento),
        formData: {
          ...formData,
          tp_situacao: status ? '1' : '0',
        },
      });
    } else {
      createEquipmentType.mutateAsync({
        formData: {
          ...formData,
          tp_situacao: status ? '1' : '0',
        },
      });
    }
    onDialogClose();
    setCurrentType(undefined);
    reset();
  }

  return (
    <Box px="8" py="4">
      <Heading>Equipamentos</Heading>
      <Flex pt={2} flexDir="column">
        <Stack py={6} gridGap={4} maxWidth="200px" w="100%">
          <ChakraButton
            fontSize="sm"
            variant="link"
            fontWeight="normal"
            color="inherit"
            textAlign="left"
            justifyContent="normal"
            onClick={onOpen}
          >
            Gerenciar tipos
          </ChakraButton>
          <Link
            fontSize="sm"
            as={RouterLink}
            to={ROUTER_PATHS.PORTAL.ADMIN.EQUIPMENT_MODELS}
          >
            Gerenciar modelos
          </Link>

          <Link
            as={RouterLink}
            fontSize="sm"
            to={ROUTER_PATHS.PORTAL.ADMIN.EQUIPMENTS}
          >
            Gerenciar equipamentos
          </Link>
          {userIsAdmin && <ActiveEquipaments />}
        </Stack>
      </Flex>
      <Drawer
        isOpen={isOpen}
        placement="right"
        onClose={onClose}
        size="lg"
        closeOnOverlayClick={false}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader mb={4}>
            <Heading fontSize="xl">Tipos de Equipamento</Heading>
          </DrawerHeader>

          <DrawerBody>
            <Flex w="100%" mb={4} justify="flex-end">
              <Button onClick={() => handleDialogCreate()}>Criar Tipo</Button>
            </Flex>
            {isLoading ? (
              <Flex justify="center">
                <Spinner />
              </Flex>
            ) : error ? (
              <Flex justify="center">
                <Text>Falha ao obter dados dos grupos.</Text>
              </Flex>
            ) : (
              <Flex flexDir="column">
                <Table type="secondary">
                  <Thead>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>Data Atualização</Th>
                      <Th>Status</Th>
                      <Th textAlign="center">Ação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data &&
                      data.results.map(type => (
                        <Tr key={type.id_tipoequipamento}>
                          <Td fontSize="14px">{type.nm_tipoequipamento}</Td>

                          <Td fontSize="14px">{type.formattedInitialDate}</Td>
                          <Td>
                            <StatusBadge variant={type.colorScheme}>
                              {type.formattedStatus}
                            </StatusBadge>
                          </Td>
                          <Td>
                            <ConfigureTypes currentType={type} />
                          </Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
                {(data?.next || data?.previous) && (
                  <Pagination
                    totalCountOfRegisters={data?.count || 0}
                    currentPage={page}
                    onPageChange={setPage}
                    registersPerPage={6}
                  />
                )}
              </Flex>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <AlertDialog
        motionPreset="slideInBottom"
        leastDestructiveRef={undefined}
        onClose={onDialogClose}
        isOpen={isDialogOpen}
        isCentered
      >
        <AlertDialogOverlay />

        <AlertDialogContent as="form" onSubmit={handleSubmit(handleSave)}>
          <AlertDialogHeader>
            {isEdition
              ? `Atualizar - ${currentType?.nm_tipoequipamento}`
              : 'Cadastrar'}
          </AlertDialogHeader>
          <AlertDialogCloseButton />
          <AlertDialogBody>
            <Stack gridGap={2}>
              <Input
                name="nm_tipoequipamento"
                label="Tipo"
                register={register}
                error={errors.nm_tipoequipamento}
                isRequired
                isDisabled={isEdition}
              />
              <FormControl
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <FormLabel htmlFor="status" mb="0">
                  {status ? 'Ativo' : 'Inativo'}
                </FormLabel>
                <Switch
                  id="status"
                  size="lg"
                  colorScheme="green"
                  {...register('tp_situacao')}
                  isChecked={status}
                  onChange={e => setStatus(e.target.checked)}
                />
              </FormControl>
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button onClick={onDialogClose} variant="secondary">
              Cancelar
            </Button>
            <Button ml={3} type="submit">
              Confirmar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Box>
  );
};
